import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const MisCuotas = () => {
  const { API, user } = useContext(AuthContext);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  useEffect(() => { loadPlanes(); }, []);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('access_token');

  const loadPlanes = async () => {
    try {
      const res = await axios.get(`${API}/planes-cuotas/mis-planes`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPlanes(res.data.planes || []);
    } catch (err) {
      console.error('Error cargando planes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubirComprobante = async (planId, numeroCuota, file) => {
    if (!file) return;
    setUploading(`${planId}-${numeroCuota}`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API}/planes-cuotas/${planId}/subir-comprobante/${numeroCuota}`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Comprobante de cuota ${numeroCuota} subido correctamente`);
      loadPlanes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir comprobante');
    } finally {
      setUploading(null);
    }
  };

  const handleDescargarContrato = async (planId) => {
    try {
      const res = await axios.get(`${API}/planes-cuotas/${planId}/contrato-pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrato_Cuotas_${planId.substring(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar contrato');
    }
  };

  const getEstadoBadge = (estado) => {
    const map = {
      pendiente: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800' },
      pendiente_aprobacion: { label: 'En Revision', cls: 'bg-blue-100 text-blue-800' },
      pagada: { label: 'Pagada', cls: 'bg-green-100 text-green-800' },
      rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-800' },
      vencida: { label: 'Vencida', cls: 'bg-red-100 text-red-800' }
    };
    const info = map[estado] || { label: estado, cls: 'bg-gray-100 text-gray-800' };
    return <Badge className={info.cls} data-testid={`cuota-estado-${estado}`}>{info.label}</Badge>;
  };

  const getDiasRestantes = (fechaVencimiento) => {
    const now = new Date();
    const venc = new Date(fechaVencimiento);
    return Math.ceil((venc - now) / (1000 * 60 * 60 * 24));
  };

  // Determine which cuota is the "current" one to pay
  const getCuotaActualIndex = (cuotas) => {
    if (!cuotas) return 0;
    for (let i = 0; i < cuotas.length; i++) {
      if (cuotas[i].estado !== 'pagada') return i;
    }
    return cuotas.length; // all paid
  };

  // Check if a cuota should be visible (paid, current, or next after current is in review)
  const esCuotaVisible = (cuotas, idx) => {
    if (!cuotas) return false;
    const cuota = cuotas[idx];
    // Always show paid or in-review cuotas
    if (['pagada', 'pendiente_aprobacion'].includes(cuota.estado)) return true;
    // Show the first non-paid cuota
    const currentIdx = getCuotaActualIndex(cuotas);
    return idx === currentIdx;
  };

  // Cuota 1 is uploaded via registration flow, not from here
  const puedeSubirDesdeAqui = (cuota) => {
    if (cuota.numero === 1) return false; // Cuota 1 se sube en el flujo de registro
    return ['pendiente', 'rechazada', 'vencida'].includes(cuota.estado);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[200px]"><i className="fas fa-spinner fa-spin text-2xl text-blue-500" /></div>;
  }

  if (planes.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <i className="fas fa-receipt text-4xl text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No tienes planes de cuotas</h3>
          <p className="text-sm text-gray-400 mt-1">Los planes de pago en 3 cuotas apareceran aqui</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="mis-cuotas-container">
      <div className="flex items-center gap-3 mb-4">
        <i className="fas fa-receipt text-2xl text-blue-600" />
        <h2 className="text-xl font-bold text-gray-800">Mis Cuotas</h2>
      </div>

      {planes.map(plan => {
        const cuotaActualIdx = getCuotaActualIndex(plan.cuotas);
        const todasPagadas = cuotaActualIdx >= (plan.cuotas?.length || 0);

        return (
          <Card key={plan.id} className="overflow-hidden" data-testid={`plan-cuotas-${plan.id}`}>
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-white text-lg">{plan.semestre_nombre}</CardTitle>
                  <p className="text-blue-100 text-sm mt-1">
                    Total: {plan.moneda} {plan.monto_total?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge className={todasPagadas ? 'bg-green-500' : 'bg-white/20'}>
                    {todasPagadas ? 'Completado' : `Cuota ${cuotaActualIdx + 1} de ${plan.cuotas?.length || 3}`}
                  </Badge>
                  {plan.contrato_pdf_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-white hover:bg-white/20 h-8"
                      onClick={() => handleDescargarContrato(plan.id)}
                      data-testid="descargar-contrato-btn"
                    >
                      <i className="fas fa-file-pdf mr-1" /> Contrato
                    </Button>
                  )}
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3">
                <div className="flex gap-1">
                  {plan.cuotas?.map((c, i) => (
                    <div key={i} className={`h-2 flex-1 rounded-full ${
                      c.estado === 'pagada' ? 'bg-green-400' :
                      c.estado === 'pendiente_aprobacion' ? 'bg-yellow-400' :
                      'bg-white/30'
                    }`} />
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {plan.cuotas?.map((cuota, idx) => {
                const visible = esCuotaVisible(plan.cuotas, idx);
                if (!visible) return null;

                const diasRestantes = getDiasRestantes(cuota.fecha_vencimiento);
                const fechaVenc = new Date(cuota.fecha_vencimiento).toLocaleDateString('es-UY');
                const puedeSubir = puedeSubirDesdeAqui(cuota);
                const isUploading = uploading === `${plan.id}-${cuota.numero}`;
                const esUrgente = diasRestantes <= 3 && diasRestantes >= 0 && puedeSubir;
                const estaVencida = diasRestantes < 0 && puedeSubir;
                const esCuotaActual = idx === cuotaActualIdx;

                return (
                  <div
                    key={idx}
                    className={`p-4 border-b last:border-b-0 ${
                      esCuotaActual && puedeSubir ? 'bg-blue-50 border-l-4 border-l-blue-500' :
                      cuota.estado === 'pagada' ? 'bg-green-50/50' :
                      estaVencida ? 'bg-red-50' :
                      esUrgente ? 'bg-amber-50' : ''
                    }`}
                    data-testid={`cuota-${cuota.numero}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          cuota.estado === 'pagada' ? 'bg-green-500' :
                          cuota.estado === 'pendiente_aprobacion' ? 'bg-blue-500' :
                          estaVencida ? 'bg-red-500' :
                          esUrgente ? 'bg-amber-500' :
                          esCuotaActual ? 'bg-blue-600' : 'bg-gray-400'
                        }`}>
                          {cuota.estado === 'pagada' ? <i className="fas fa-check" /> :
                           cuota.estado === 'pendiente_aprobacion' ? <i className="fas fa-clock" /> :
                           cuota.numero}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            Cuota {cuota.numero} - {cuota.moneda || plan.moneda} {cuota.monto?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-sm text-gray-500">
                            Vence: {fechaVenc}
                            {puedeSubir && diasRestantes >= 0 && (
                              <span className={`ml-2 font-medium ${diasRestantes <= 3 ? 'text-amber-600' : 'text-gray-500'}`}>
                                ({diasRestantes} dias)
                              </span>
                            )}
                            {estaVencida && (
                              <span className="ml-2 font-bold text-red-600">VENCIDA</span>
                            )}
                          </p>
                          {cuota.estado === 'rechazada' && cuota.motivo_rechazo && (
                            <p className="text-xs text-red-600 mt-1">
                              <i className="fas fa-exclamation-circle mr-1" />
                              {cuota.motivo_rechazo}
                            </p>
                          )}
                          {cuota.estado === 'pendiente_aprobacion' && (
                            <p className="text-xs text-blue-600 mt-1">
                              <i className="fas fa-info-circle mr-1" />
                              Comprobante enviado, esperando aprobacion del administrador
                            </p>
                          )}
                          {esCuotaActual && puedeSubir && (
                            <p className="text-xs text-blue-700 font-medium mt-1">
                              <i className="fas fa-arrow-right mr-1" />
                              Esta es tu cuota actual a pagar
                            </p>
                          )}
                          {cuota.numero === 1 && cuota.estado === 'pendiente' && (
                            <p className="text-xs text-gray-500 mt-1">
                              <i className="fas fa-info-circle mr-1" />
                              Este recibo se sube en el flujo de registro
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getEstadoBadge(cuota.estado)}
                        {puedeSubir && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handleSubirComprobante(plan.id, cuota.numero, e.target.files?.[0])}
                              disabled={isUploading}
                            />
                            <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors ${
                              isUploading ? 'bg-gray-400' : estaVencida ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                            }`} data-testid={`subir-comprobante-${cuota.numero}`}>
                              {isUploading ? (
                                <><i className="fas fa-spinner fa-spin" /> Subiendo...</>
                              ) : (
                                <><i className="fas fa-upload" /> Subir Comprobante</>
                              )}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Show upcoming cuotas as locked */}
              {plan.cuotas?.map((cuota, idx) => {
                const visible = esCuotaVisible(plan.cuotas, idx);
                if (visible) return null; // already rendered above
                const fechaVenc = new Date(cuota.fecha_vencimiento).toLocaleDateString('es-UY');
                return (
                  <div key={`locked-${idx}`} className="p-4 border-b last:border-b-0 bg-gray-50 opacity-60" data-testid={`cuota-bloqueada-${cuota.numero}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-300 text-white font-bold">
                        <i className="fas fa-lock text-sm" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-500">
                          Cuota {cuota.numero} - {cuota.moneda || plan.moneda} {cuota.monto?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-gray-400">
                          Vence: {fechaVenc} - Se habilitara al pagar la cuota anterior
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MisCuotas;
